
import { useEffect, useRef, useState } from "react";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import CropModal from "./CropModal";
import ProgressBar from "./ProgressBar";

const ImageUploader = ({ uploadEndpoint, onUploadComplete }) => {
    const axiosPrivate = useAxiosPrivate();
    const [filesToUpload, setFilesToUpload] = useState([]); // { file, preview }
    const [progressMap, setProgressMap] = useState({});
    const [cropFile, setCropFile] = useState(null);
    const inputRef = useRef();

    useEffect(() => {
        return () => filesToUpload.forEach(f => URL.revokeObjectURL(f.preview));
    }, [filesToUpload]);

    const addFiles = (list) => {
        const arr = Array.from(list).map(f => ({ file: f, preview: URL.createObjectURL(f) }));
        setFilesToUpload(prev => [...prev, ...arr]);
    };

    const onSelect = (e) => addFiles(e.target.files);
    const handleDrop = (e) => { e.preventDefault(); addFiles(e.dataTransfer.files); };

    const uploadAll = async () => {
        if (!filesToUpload.length) return alert("No files selected");
        const form = new FormData();
        filesToUpload.forEach(f => form.append("images", f.file));
        try {
            await axiosPrivate.post(`${uploadEndpoint}/images`, form, {
                headers: { "Content-Type": "multipart/form-data" },
                onUploadProgress: (p) => setProgressMap({ overall: Math.round((p.loaded * 100) / p.total) })
            });
            setFilesToUpload([]);
            setProgressMap({});
            onUploadComplete();
        } catch (err) {
            console.error(err);
        }
    };

    const uploadSingle = async (fileObj) => {
        const form = new FormData();
        form.append("image", fileObj.file);
        try {
            await axiosPrivate.post(`${uploadEndpoint}/image`, form, {
                headers: { "Content-Type": "multipart/form-data" },
                onUploadProgress: (p) => setProgressMap(prev => ({ ...prev, [fileObj.file.name]: Math.round((p.loaded * 100) / p.total) }))
            });
            onUploadComplete();
        } catch (err) { console.error(err); }
    };

    const removeFile = (name) => setFilesToUpload(prev => prev.filter(f => f.file.name !== name));

    const onCropped = (blob, newName, originalObj) => {
        const newFile = new File([blob], newName || originalObj.file.name, { type: blob.type });
        const newPreview = URL.createObjectURL(blob);
        setFilesToUpload(prev => prev.map(p => p.file.name === originalObj.file.name ? { file: newFile, preview: newPreview } : p));
        setCropFile(null);
    };

    return (
        <div className="imageUploader">
            <div className="dropzone"
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleDrop}
                onClick={() => inputRef.current.click()}>
                <input ref={inputRef} type="file" multiple accept="image/*" onChange={onSelect} style={{ display: "none" }} />
                <p>Click or drag images here</p>
            </div>

            {filesToUpload.length > 0 && (
                <>
                    <div className="previewGrid">
                        {filesToUpload.map((item, idx) => (
                            <div className="previewCard" key={idx}>
                                <img src={item.preview} alt={item.file.name} onClick={() => setCropFile(item)} />
                                <div className="btnRow">
                                    <button onClick={() => setCropFile(item)}>Crop</button>
                                    <button onClick={() => uploadSingle(item)}>Upload</button>
                                    <button onClick={() => removeFile(item.file.name)}>Remove</button>
                                </div>
                                {progressMap[item.file.name] && <ProgressBar progress={progressMap[item.file.name]} />}
                            </div>
                        ))}
                    </div>

                    <button className="uploadAllBtn" onClick={uploadAll}>Upload All</button>
                </>
            )}

            {progressMap.overall > 0 && <ProgressBar progress={progressMap.overall} />}

            {cropFile && <CropModal file={cropFile.file} onClose={() => setCropFile(null)} onCropped={(b, name) => onCropped(b, name, cropFile)} />}
        </div>
    );
};

export default ImageUploader;
